import Vue from "vue";
import { httpsCallable } from "firebase/functions";
import { getOhmyartApp } from "@/ohmyartApp";
import { EventBus, events } from "../event-bus";

/**
 * Laufende Generierungen (generateArtworkV9) und Ergebnisse — außerhalb der Komponente, damit der
 * Generieren-Dialog im Header geschlossen werden kann, während Läufe weiterlaufen, und damit der
 * Header die Anzahl laufender Generierungen anzeigen kann. Mehrere Läufe parallel sind erlaubt.
 */
const STORAGE_KEY = "taskdash_generate_results_v1";
export const MAX_PARALLEL_RUNS = 4;

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

const loadResults = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(saved) ? saved.map((r) => ({ ...r, up: { ...(r.up || {}), starting: false } })) : [];
  } catch (e) {
    return [];
  }
};

export const generateRuns = Vue.observable({
  /** laufende Generierungen: { id, startedAt, styleId, version, orientation, inputs, combined } */
  runs: [],
  /** fertige Ergebnisse, neueste zuerst (wie bisher in localStorage) */
  results: loadResults(),
  /** letzter Fehler (wird im Dialog angezeigt, bis der nächste Lauf startet) */
  lastError: "",
});

export const persistResults = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(generateRuns.results.slice(0, 40)));
  } catch (e) {
    /* voll */
  }
};

export const removeResult = (id) => {
  generateRuns.results = generateRuns.results.filter((r) => r.id !== id);
  persistResults();
};

/** Generierung starten; läuft unabhängig von der Komponente weiter. Gibt den Lauf zurück (oder null, wenn voll). */
export function startGeneration({ styleId, version, orientation, paths, inputs, meta, overrides }) {
  // Admin-Override (nur wirksam für ADMIN_UIDS in generateArtworkV9): leere Werte = Shop-Standard
  const ov = {};
  if (overrides && ["2K", "4K"].includes(overrides.resolution)) ov.resolution = overrides.resolution;
  if (overrides && ["low", "medium", "high"].includes(overrides.quality)) ov.quality = overrides.quality;
  const hasOverrides = Object.keys(ov).length > 0;
  if (generateRuns.runs.length >= MAX_PARALLEL_RUNS) {
    EventBus.$emit(events.SNACKBAR, { message: `Höchstens ${MAX_PARALLEL_RUNS} Generierungen gleichzeitig`, type: "error" });
    return null;
  }
  const run = { id: uuid(), startedAt: Date.now(), styleId, version, orientation, inputs, combined: paths.length >= 2, overrides: hasOverrides ? ov : null };
  generateRuns.runs = [...generateRuns.runs, run];
  generateRuns.lastError = "";
  (async () => {
    try {
      const { functions } = getOhmyartApp();
      const call = httpsCallable(functions, "generateArtworkV9", { timeout: 540000 });
      const payload = {
        styleId,
        version,
        aspectRatio: orientation === "landscape" ? "3:2" : "2:3",
        requestId: uuid().replace(/-/g, ""),
        ...(paths.length >= 2 ? { storagePaths: paths, storagePath: paths[0] } : { storagePath: paths[0] }),
        ...(hasOverrides ? { overrides: ov } : {}),
      };
      const { data } = await call(payload);
      if (!data || !data.url) throw new Error("Keine Bild-URL in der Antwort");
      generateRuns.results = [
        {
          id: uuid(),
          at: Date.now(),
          ms: Date.now() - run.startedAt,
          styleId,
          version,
          orientation,
          inputs,
          combined: run.combined,
          url: data.url,
          opaqueUrl: data.opaqueUrl || null,
          docId: data.docId || null,
          requestId: data.requestId || payload.requestId,
          cutout: data.cutout || "none",
          overrides: hasOverrides ? ov : null,
          meta,
          view: "final",
          bg: data.cutout === "done" ? "dark" : "light",
          up: {
            engine: "default",
            resolution: "4K",
            detailPass: !!(meta && meta.print && meta.print.detailPass && meta.print.detailPass.enabled),
            cutout: !!(meta && meta.transparent),
            jobId: null,
            starting: false,
            error: "",
          },
        },
        ...generateRuns.results,
      ];
      persistResults();
      EventBus.$emit(events.SNACKBAR, { message: `Generierung fertig: ${styleId} v${version}`, type: "success" });
    } catch (e) {
      const message = e.message || String(e);
      generateRuns.lastError = `${styleId} v${version}: ${message}`;
      EventBus.$emit(events.SNACKBAR, { message: `Generierung fehlgeschlagen: ${message}`, type: "error" });
    } finally {
      generateRuns.runs = generateRuns.runs.filter((r) => r.id !== run.id);
    }
  })();
  return run;
}
